import React, { useEffect, useState } from 'react'

import { message, Select, Spin } from 'antd'
import { FaSearch } from 'react-icons/fa'
import { TbMessageQuestion } from 'react-icons/tb'

import './styles.scss'
import CustomTable from '../../../assets/component/table'
import { useDispatch, useSelector } from 'react-redux'
import { GetCataloguesAction, GetCataloguesReset } from '../../../store/slices/ai-chat/prompts'
import ManageFilters from '../../../assets/component/manage-filters'
import { decryptData } from '../../../helpers/encrypt/crypto-encrypt'

export const fetchStoredTabs = ({ playbookName }) => {
    let license_decrypt = decryptData(sessionStorage.getItem('license'))
    const sessionStoredTabDetails = license_decrypt?.accessLimits
    const storedPlaybookDetails = JSON.parse(sessionStoredTabDetails)
    const currentPlaybookTabs = storedPlaybookDetails?.playBooks?.find((item) => item?.key === playbookName)?.value

    return currentPlaybookTabs
}

const PlaybookMain = ({ setQuestionInput, setPresetQuestion, isModalContent, handleShowPromptsModal, promptType, promptEvaluation, chunkEvaluation }) => {
    const [search, setSearch] = useState("")
    const [selectedPlaybook, setSelectedPlaybook] = useState([])
    const [selectedKeyIssue, setSelectedKeyIssue] = useState([])
    const [promptTableData, setPromptTableData] = useState([])
    const [page, setPage] = useState(1)
    const [limit, setLimit] = useState(50)
    const [showFilters, setShowFilters] = useState(true)
    const [tableStats, setTableStats] = useState({ totalPages: '', totalRecords: '' })

    const dispatch = useDispatch()
    const { data: promptData, isLoading: promptLoading, error: promptError } = useSelector((state) => state.catalogues)

    const playbookModalTableHeaders = [
        {
            title: 'Playbook',
            dataIndex: 'frameworkName',
            key: 'frameworkName',
            width: 150,
        },
        {
            title: 'Key Issue / Theme',
            dataIndex: 'keyIssue',
            key: 'age',
            width: 150,
        },
        {
            title: 'Question',
            dataIndex: 'question',
            key: 'question',
            render: (_, { question }) => {
                const texts = question.split(new RegExp(`(${search})`, 'gi'))
                return (
                    <>
                        <div>
                            {texts.map((text, index) =>
                                search && text.toLowerCase() === search.toLowerCase() ? (
                                    <span key={index} className="text-highlight">
                                        {text}
                                    </span>
                                ) : (
                                    text
                                ),
                            )}
                        </div>
                    </>
                )
            },
        },
        {
            title: '',
            dataIndex: 'actions',
            key: 'actions',
            width: 100,
        },
    ]

    const playbookPageTableHeaders = [
        {
            title: 'Key Issue',
            dataIndex: 'keyIssue',
            key: 'age',
            width: 300,
        },
        {
            title: 'Question',
            dataIndex: 'question',
            key: 'question',
            render: (_, { question }) => {
                const texts = question.split(new RegExp(`(${search})`, 'gi'))
                return (
                    <>
                        <div>
                            {texts.map((text, index) =>
                                search && text.toLowerCase() === search.toLowerCase() ? (
                                    <span key={index} className="text-highlight">
                                        {text}
                                    </span>
                                ) : (
                                    text
                                ),
                            )}
                        </div>
                    </>
                )
            },
        },
        {
            title: '',
            dataIndex: 'actions',
            key: 'actions',
            width: 100,
        },
    ]

    const requestPrompts = () => {
        dispatch(GetCataloguesReset())

        const payload = {
            frameworkName: !isModalContent ? promptType : selectedPlaybook?.length > 0 ? selectedPlaybook : '',
            keyIssue: selectedKeyIssue?.length > 0 ? selectedKeyIssue : '',
            question: search,
            page: page,
            limit: limit,
        }

        dispatch(GetCataloguesAction(payload))
    }

    const resetFilters = async () => {
        setSearch('')
        setSelectedKeyIssue([])
        setSelectedPlaybook([])
        setPage(1)
        setLimit(limit)

        const payload = {
            frameworkName: !isModalContent ? promptType : '',
            keyIssue: '',
            question: '',
            page: 1,
            limit: limit,
        }

        dispatch(GetCataloguesAction(payload))
    }

    const handleChangePlaybook = (e) => {
        setSelectedPlaybook(e)
    }

    const handleKeyIssueChange = (e) => {
        setSelectedKeyIssue(e)
    }

    const handleSearch = (e) => {
        setSearch(e.target.value)
    }

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault()
            requestPrompts()
        }
    }

    const handleRowSelection = ({ selectedRow }) => {
        if (selectedRow?.question) {            
            setPresetQuestion(selectedRow?.presetQuestion)
            setQuestionInput(selectedRow?.question)

            if (handleShowPromptsModal) {
                handleShowPromptsModal()
            }
        }
    }

    const formatTableRow = ({ rowDetails }) => {
        const ActionItems = () => {
            return (
                <div className="action-buttons-container">
                    <div className="action-button regenerate-question" onClick={() => handleRowSelection({ selectedRow: rowDetails })}>
                        Ask <TbMessageQuestion className="icon" />
                    </div>
                </div>
            )
        }

        return {
            ...rowDetails,
            keyIssue: rowDetails?.keyIssue || rowDetails?.theme,
            actions: <ActionItems />,
        }
    }

    const handleShowFiltersSection = () => {
        setShowFilters(!showFilters)
    }

    const handleResize = () => {
        if (window.innerWidth > 1024) {
            setShowFilters(true)
        }
    }

    useEffect(() => {
        handleResize()
        window.addEventListener('resize', handleResize)
        return () => {
            window.removeEventListener('resize', handleResize) //Clean up
        }
    }, [])

    useEffect(() => {
        if (!promptEvaluation && !chunkEvaluation) {
            requestPrompts()
        }
    }, [page, limit, selectedKeyIssue, selectedPlaybook, promptType])

    useEffect(() => {
        const structuredData = promptData?.questionSet?.map((row) => formatTableRow({ rowDetails: row }))
        setPromptTableData({ dataSource: structuredData, columns: isModalContent ? playbookModalTableHeaders : playbookPageTableHeaders })
    }, [promptData?.questionSet])

    useEffect(() => {
        setTableStats({
            totalPages: promptData?.totalPages,
            totalRecords: promptData?.totalRecords,
        })
    }, [promptData])

    useEffect(() => {
        if (promptError?.message) {
            message.error(promptError?.message)
        }
    }, [promptError?.message])

    return (
        <div className={`promt-main-container ${isModalContent ? 'prompt-main-modal' : 'prompt-main-full-screen'}`}>
            <div className="prompt-filters-section-wrapper">
                {showFilters && (
                    <div className="prompt-filters-section">
                        {isModalContent && (
                            <div className="input-wrapper">
                                <Select
                                    showSearch
                                    allowClear
                                    className="prompt-select"
                                    popupClassName="prompt-react-select"
                                    options={promptData?.frameworks || []}
                                    onChange={(e) => handleChangePlaybook(e)}
                                    value={selectedPlaybook}
                                    placeholder="Select Playbook"
                                    maxLength={2}
                                    filterOption={(input, option) => option?.label?.toLowerCase()?.includes(input?.toLowerCase())}
                                    notFoundContent={promptLoading ? <div className="dropdown-loading"><Spin size="small" /></div> : "No Data"}
                                />
                            </div>
                        )}
                        <div className="input-wrapper">
                            <Select
                                showSearch
                                allowClear
                                className="prompt-select"
                                popupClassName="prompt-react-select"
                                options={promptData?.keyIssues || []}
                                onChange={handleKeyIssueChange}
                                value={selectedKeyIssue}
                                placeholder="Select Key Issue / Theme"
                                maxLength={2}
                                filterOption={(input, option) => option?.label?.toLowerCase()?.includes(input?.toLowerCase())}
                                notFoundContent={promptLoading ? <div className="dropdown-loading"><Spin size="small" /></div> : "No Data"}
                            />
                        </div>
                        <div className="search-wrapper">
                            <input type="text" className="search-input" placeholder="Search Question" value={search} onChange={(e) => handleSearch(e)} onKeyDown={(e) => handleKeyPress(e)} />
                            <FaSearch className={`search-icon ${search ? 'active-icon' : ''}`} onClick={() => requestPrompts()} />
                        </div>
                        <div className="prompt-filter-buttons">
                            <div className="filter-button apply-button" onClick={() => requestPrompts()}>
                                Apply
                            </div>
                            <div className="filter-button reset-button" onClick={() => resetFilters()}>
                                Reset
                            </div>
                        </div>
                    </div>
                )}
                <ManageFilters showFilters={showFilters} handleShowFiltersSection={handleShowFiltersSection} />
            </div>
            <div className="prompt-main-body">
                <CustomTable isLoading={promptLoading} limit={limit} setLimit={setLimit} page={page} setPage={setPage} totalRows={tableStats?.totalRecords} tableData={promptTableData} />
            </div>
        </div>
    )
}

export default PlaybookMain
